import PropTypes from 'prop-types';
import { useFieldArray } from 'react-hook-form';
import { PiPlusBold, PiTrashBold } from 'react-icons/pi';

import { cn } from '#/lib/utils';
import {
  Button,
  DESTRUCTIVE_GHOST_BUTTON_CLASS,
} from '#/modules/commonComponents/button';
import PlainRegisteredInput from '#/modules/commonComponents/form/PlainRegisteredInput';
import { createEmptyExtraItemRow } from '#/modules/crmMgmt/product/helper/buildProductWritePayload';

const EXTRA_ITEM_COLS =
  '@lg/extra-item:grid-cols-[minmax(0,1fr)_6rem_6rem_minmax(0,1fr)_auto]';

const EXTRA_ITEM_HEADER_CLASS = cn(
  'sticky top-0 z-[1] hidden w-full gap-3 border-b border-gray-300 bg-gray-100 px-4 py-2',
  'text-xs font-medium text-gray-600',
  '@lg/extra-item:grid',
  EXTRA_ITEM_COLS,
);

/** 僅 max-h 內捲；勿套 form-list--window（其含 min-height） */
const PRODUCT_LIST_MAX_HEIGHT_STYLE = {
  maxHeight: 'min(32rem, calc(100vh - var(--form-list-chrome)))',
};

const ProductExtraItemRow = ({
  index,
  register,
  errors,
  onRemove,
}) => (
  <div className="border-b border-gray-200 px-4 py-2.5 last:border-b-0">
    <div
      className={cn(
        'grid grid-cols-1 items-center gap-3',
        EXTRA_ITEM_COLS,
      )}
    >
      <div className="min-w-0">
        <PlainRegisteredInput
          id={`extraItems.${index}.itemName`}
          type="text"
          register={register}
          errors={errors}
          mobileLabel="項目名稱"
          mobileLabelClassName="@lg/extra-item:hidden"
          ariaLabel={`項目名稱 ${index + 1}`}
        />
      </div>
      <div className="min-w-0">
        <PlainRegisteredInput
          id={`extraItems.${index}.qtyPerProduct`}
          type="number"
          min={0}
          step="any"
          register={register}
          errors={errors}
          mobileLabel="數量"
          mobileLabelClassName="@lg/extra-item:hidden"
          ariaLabel={`數量 ${index + 1}`}
        />
      </div>
      <div className="min-w-0">
        <PlainRegisteredInput
          id={`extraItems.${index}.unitName`}
          type="text"
          register={register}
          errors={errors}
          mobileLabel="單位"
          mobileLabelClassName="@lg/extra-item:hidden"
          ariaLabel={`單位 ${index + 1}`}
        />
      </div>
      <div className="min-w-0">
        <PlainRegisteredInput
          id={`extraItems.${index}.remark`}
          type="text"
          register={register}
          errors={errors}
          mobileLabel="備註"
          mobileLabelClassName="@lg/extra-item:hidden"
          ariaLabel={`備註 ${index + 1}`}
        />
      </div>
      <div className="flex min-w-0 @lg/extra-item:justify-end">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className={DESTRUCTIVE_GHOST_BUTTON_CLASS}
          aria-label={`移除附加項目 ${index + 1}`}
          onClick={() => onRemove(index)}
        >
          <PiTrashBold className="size-4" />
        </Button>
      </div>
    </div>
  </div>
);

ProductExtraItemRow.propTypes = {
  index: PropTypes.number.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  onRemove: PropTypes.func.isRequired,
};

const ProductExtraItemsEditor = ({
  control,
  register,
  errors,
}) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'extraItems',
    keyName: 'fieldKey',
  });

  function handleAdd() {
    append(createEmptyExtraItemRow());
  }

  return (
    <section className="form-section @container/form-section">
      <div className="form-section__header">
        <h2 className="form-block__title mb-0!">附加項目</h2>
        <Button type="button" size="sm" onClick={handleAdd}>
          <PiPlusBold aria-hidden />
          新增
        </Button>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        可以不新增；本身不扣庫存。
      </p>

      {errors?.extraItems?.message ? (
        <p className="mt-2 text-sm text-red-500">{errors.extraItems.message}</p>
      ) : null}

      <div className="form-list mt-3" style={PRODUCT_LIST_MAX_HEIGHT_STYLE}>
        <div className="form-list__head">
          <p className="font-medium">附加項目</p>
          <p className="text-xs text-gray-500">
            {fields.length}
            {' '}
            筆
          </p>
        </div>
        <div className={cn('form-list__body', '@container/extra-item')}>
          {fields.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-muted-foreground">
              尚未新增附加項目；儲存時以空陣列送出。
            </p>
          ) : (
            <>
              <div className={EXTRA_ITEM_HEADER_CLASS} aria-hidden>
                <span className="min-w-0">項目名稱</span>
                <span className="min-w-0">數量</span>
                <span className="min-w-0">單位</span>
                <span className="min-w-0">備註</span>
                <span className="sr-only">操作</span>
              </div>
              {fields.map((field, index) => (
                <ProductExtraItemRow
                  key={field.fieldKey}
                  index={index}
                  register={register}
                  errors={errors}
                  onRemove={remove}
                />
              ))}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

ProductExtraItemsEditor.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
};

export default ProductExtraItemsEditor;
